import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  BookOpen,
  BriefcaseBusiness,
  ChevronRight,
  FileText,
  Globe,
  Lock,
  type LucideIcon,
  Map,
  MapPin,
  Navigation,
  Settings,
  UserRound,
  UsersRound,
} from "lucide-react";

import { DashboardStats } from "@/components/dashboard-summary";
import { Page, PageHeader, SectionHeader } from "@/components/ui/page";
import { cn } from "@/utils/utils";
import type { ComingSoonSlug } from "@/pages/ComingSoonPage";
import type { TranslationKey } from "@/constants/i18n";
import { can } from "@/utils/permissions";
import { profileQuery } from "@/services/queries";
import { useLocaleStore, useTranslation } from "@/store/locale";

/** A tile leads to a real route, or to that section's coming-soon page. */
interface Tile {
  icon: LucideIcon;
  title: TranslationKey;
  text: TranslationKey;
  /** Codename the tile's screen is guarded by; without it the tile is restricted. */
  perm?: string;
  to?:
    | "/provinces"
    | "/cities"
    | "/barangays"
    | "/configuration"
    | "/employees"
    | "/employee-activity-log"
    | "/member-activity-log"
    | "/yellow-page"
    | "/non-members"
    | "/members";
  slug?: ComingSoonSlug;
}

/**
 * The four things people come here to start. They are the only tiles that get
 * real weight — a dashboard where every destination looks equally urgent is a
 * dashboard that has made no decision on the reader's behalf.
 */
const QUICK: Tile[] = [
  {
    icon: UsersRound,
    title: "dash.members",
    perm: "view__member",
    text: "dash.membersText",
    to: "/members",
  },
  {
    icon: BriefcaseBusiness,
    title: "dash.employees",
    perm: "view__user",
    text: "dash.employeesText",
    to: "/employees",
  },
  {
    icon: UserRound,
    title: "dash.nonMember",
    perm: "view__non_member",
    text: "dash.nonMemberText",
    to: "/non-members",
  },
];

const LOCATION: Tile[] = [
  {
    icon: Globe,
    title: "dash.province",
    perm: "view__province",
    text: "dash.provinceText",
    to: "/provinces",
  },
  {
    icon: MapPin,
    title: "dash.city",
    perm: "view__city",
    text: "dash.cityText",
    to: "/cities",
  },
  {
    icon: Navigation,
    title: "dash.barangay",
    perm: "view__barangay",
    text: "dash.barangayText",
    to: "/barangays",
  },
  {
    icon: Activity,
    title: "dash.employeeLog",
    perm: "view__employee_activity_log",
    text: "dash.employeeLogText",
    to: "/employee-activity-log",
  },
  {
    icon: FileText,
    title: "dash.memberLog",
    perm: "view__member_log",
    text: "dash.memberLogText",
    to: "/member-activity-log",
  },
];

const WORKSPACE: Tile[] = [
  {
    icon: BookOpen,
    title: "dash.yellowPage",
    perm: "view__yellow_page",
    text: "dash.yellowPageText",
    to: "/yellow-page",
  },
  { icon: Map, title: "dash.maps", text: "dash.mapsText", slug: "maps" },
  {
    icon: Settings,
    title: "dash.configuration",
    text: "dash.configurationText",
    to: "/configuration",
  },
];

function DashboardPage() {
  const t = useTranslation();
  const locale = useLocaleStore((state) => state.locale);
  const today = new Date().toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <Page>
      <PageHeader eyebrow={today} title={t("dash.title")} description={t("dash.kicker")} />

      <DashboardStats />

      {/* The primary row: big enough to be the answer to "what now?". */}
      <div className="mb-10 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {QUICK.map((tile) => (
          <QuickTile key={tile.title} {...tile} />
        ))}
      </div>

      {/* Nothing sits beside these now, so they take the width and the rows
          run three across — two columns of half-empty rows on a wide screen
          reads as a column that lost its neighbour. */}
      <div className="grid gap-8">
        <section>
          <SectionHeader title={t("dash.location")} description={t("dash.locationText")} />
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {LOCATION.map((tile) => (
              <TileRow key={tile.title} {...tile} />
            ))}
          </div>
        </section>
        <section>
          <SectionHeader title={t("dash.workspace")} description={t("dash.workspaceText")} />
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {WORKSPACE.map((tile) => (
              <TileRow key={tile.title} {...tile} />
            ))}
          </div>
        </section>
      </div>
    </Page>
  );
}

/** Permission decides whether a tile is a door or a closed one, never whether it exists. */
function useAllowed(perm?: string) {
  const { data: profile } = useQuery(profileQuery());
  // Open until the profile answers: a tile is a door, and a closed door is a
  // judgement, so it waits for the facts before closing.
  return !perm || !profile || can(profile, perm);
}

function QuickTile({ icon: Icon, title, text, to, slug, perm }: Tile) {
  const t = useTranslation();
  const allowed = useAllowed(perm);
  const body = (
    <>
      <span
        className={cn(
          "grid size-10 place-items-center rounded-[12px] transition-colors",
          allowed ? "bg-accent-soft text-accent" : "bg-surface-inset text-fg-subtle",
        )}
      >
        {allowed ? (
          <Icon className="size-5" aria-hidden />
        ) : (
          <Lock className="size-5" aria-hidden />
        )}
      </span>
      <span className="mt-3.5 block">
        <span className="text-title-3 text-heading block">{t(title)}</span>
        <span className="text-fg-muted text-subhead mt-1 block">
          {allowed ? t(text) : t("dash.restricted")}
        </span>
      </span>
    </>
  );
  const base =
    "rounded-card bg-surface-raised block p-4 transition-[transform,box-shadow] duration-[var(--duration-base)] ease-[var(--ease-out-soft)]";
  if (!allowed) {
    return (
      <div
        aria-disabled
        title={t("dash.restrictedHint")}
        className={cn(base, "border-line cursor-not-allowed border border-dashed opacity-70")}
      >
        {body}
      </div>
    );
  }
  const props = {
    className: cn(
      base,
      "shadow-[var(--shadow-lift)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-raised)]",
    ),
  };
  return to ? (
    <Link to={to} {...props}>
      {body}
    </Link>
  ) : (
    <Link to={`/${slug ?? ""}`} {...props}>
      {body}
    </Link>
  );
}

function TileRow({ icon: Icon, title, text, to, slug, perm }: Tile) {
  const t = useTranslation();
  const allowed = useAllowed(perm);
  const base =
    "rounded-card flex min-w-0 items-center gap-3 px-3 py-2.5 transition-colors duration-[var(--duration-fast)]";
  const body = (
    <>
      <span
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-[10px]",
          allowed ? "bg-surface-inset text-fg-muted" : "bg-surface-inset text-fg-subtle",
        )}
      >
        {allowed ? (
          <Icon className="size-4.5" aria-hidden />
        ) : (
          <Lock className="size-4.5" aria-hidden />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-fg text-callout block truncate font-medium">{t(title)}</span>
        <span className="text-fg-muted text-subhead block truncate">
          {allowed ? t(text) : t("dash.restricted")}
        </span>
      </span>
      <ChevronRight className="text-fg-subtle size-4 shrink-0" aria-hidden />
    </>
  );
  if (!allowed) {
    return (
      <div
        aria-disabled
        title={t("dash.restrictedHint")}
        className={cn(base, "cursor-not-allowed opacity-60")}
      >
        {body}
      </div>
    );
  }
  const props = {
    className: cn(base, "hover:bg-surface-raised hover:shadow-[var(--shadow-lift)]"),
  };
  return to ? (
    <Link to={to} {...props}>
      {body}
    </Link>
  ) : (
    <Link to={`/${slug ?? ""}`} {...props}>
      {body}
    </Link>
  );
}

export default DashboardPage;
